import { blocksUnitOccupation } from "./cannonTile";
import { getTile, inBounds, manhattan, neighbors, unitAt } from "./grid";
import type { Coord, GameState, Tile, Unit, UnitKind } from "./types";
import { isFootUnit } from "./units";

const TRANSPORT_KINDS = ["landTransport", "transportCopter"] as const;

export function isArmedLandTransport(unit: Pick<Unit, "kind" | "cargo">): boolean {
  return unit.kind === "landTransport" && unit.cargo !== undefined && isFootUnit(unit.cargo.kind);
}

export function canPrepareUnload(unit: Unit): boolean {
  return (
    isTransport(unit.kind) && unit.cargo !== undefined && !unit.hasAttacked && !unit.unloadReady
  );
}

export function shouldAutoPrepareUnload(unit: Unit): boolean {
  return canPrepareUnload(unit);
}

export function isTransport(kind: UnitKind): boolean {
  return (TRANSPORT_KINDS as readonly string[]).includes(kind);
}

function isLandTile(tile: Tile): boolean {
  return tile.kind !== "sea";
}

function transportCanCarry(transport: Unit, passengerKind: UnitKind): boolean {
  if (!isTransport(transport.kind)) return false;
  if (transport.cargo) return false;
  return isFootUnit(passengerKind);
}

function canFootUnitEnterTile(tile: Tile): boolean {
  return tile.kind !== "sea";
}

export function canUnloadAt({
  state,
  transport,
  pos,
}: {
  state: GameState;
  transport: Unit;
  pos: Coord;
}): boolean {
  if (!transport.cargo) return false;
  if (!transport.unloadReady) return false;
  if (!inBounds(state, pos)) return false;
  if (manhattan(transport.pos, pos) !== 1) return false;
  if (unitAt(state, pos)) return false;
  if (blocksUnitOccupation(getTile(state, pos))) return false;

  const transportTile = getTile(state, transport.pos);
  const destTile = getTile(state, pos);
  if (!isLandTile(destTile)) return false;
  if (!canFootUnitEnterTile(destTile)) return false;

  if (transport.kind === "transportCopter" && !isLandTile(transportTile)) {
    return false;
  }

  return true;
}

export function computeUnloadTiles(state: GameState, transport: Unit): Coord[] {
  if (!transport.cargo || !transport.unloadReady) return [];
  const tiles: Coord[] = [];
  for (const n of neighbors(transport.pos)) {
    if (canUnloadAt({ state, transport, pos: n })) {
      tiles.push(n);
    }
  }
  return tiles;
}

export function canLoadInto({
  state: _state,
  passenger,
  transport,
}: {
  state: GameState;
  passenger: Unit;
  transport: Unit;
}): boolean {
  if (!isFootUnit(passenger.kind)) return false;
  if (passenger.hasMoved || passenger.hasAttacked) return false;
  if (!transportCanCarry(transport, passenger.kind)) return false;
  if (passenger.owner !== transport.owner) return false;
  return true;
}
